/* eslint-disable max-len */
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';

import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { UserProfileHttpService } from '@sondermind/data-access/therapist-profile';

import { ButtonsModule } from '@sondermind/features/design/buttons';
import { JourneyModule } from '@sondermind/features/design/journey-header';
import { IrisButtonModule } from '@sondermind/iris/button';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';
import { IrisIconComponent } from '@sondermind/iris/icon';

/* eslint-disable max-len */
import { IrisMenuModule } from '@sondermind/iris/menu';
import { IrisIconButtonModule } from '@sondermind/iris/icon-button';
import { CoreModule } from './core.module';
import { CardBrandLogoPathPipe } from './design/card-brand-logo-path.pipe';
import * as CardCarousel from './design/card-carousel/card-carousel.component';
import * as CardLayout from './design/card-layout.component';
import * as DataTable from './design/data-table/data-table.component';
import { GridLayoutComponent } from './design/grid-layout/grid-layout.component';
import { IconLabelComponent } from './design/icon-label.component';
import { IconLoaderComponent } from './design/icon-loader.component';
import { ImageUploaderComponent } from './design/image-uploader/image-uploader.component';
import { ModalComponent } from './design/modal/modal.component';
import { PaginationControlComponent } from './design/pagination-control/pagination-control.component';
import { PopupComponent } from './design/popup/popup.component';
import { PopupTriggerDirective } from './design/popup/trigger.directive';
import { ProgressScreenComponent } from './design/progress-screen/progress-screen.component';
// eslint-disable-next-line max-len
import { ReferenceDatumChecklistComponent } from './design/reference-datum-checklist/reference-datum-checklist.component';
import { SonderModalModule } from './design/sonder-modal/sonder-modal.module';
import { SuccessErrorStateBannerComponent } from './design/success-error-banner/success-error-banner.component';
import { TimePickerDirective } from './design/time-picker/directive/time-picker.directive';
import { TimePickerComponent } from './design/time-picker/time-picker.component';
import { StaticToastComponent } from './design/toast/toast-static.component';
import { ToastComponent } from './design/toast/toast.component';
import * as Typography from './design/typography.directive';

const COMPONENTS = [
  CardBrandLogoPathPipe,
  ...CardCarousel.Declarations,
  ...CardLayout.Declarations,
  ...DataTable.Declarations,
  IconLabelComponent,
  IconLoaderComponent,
  ImageUploaderComponent,
  ModalComponent,
  SuccessErrorStateBannerComponent,
  PaginationControlComponent,
  PopupComponent,
  PopupTriggerDirective,
  ProgressScreenComponent,
  ReferenceDatumChecklistComponent,
  GridLayoutComponent,
  StaticToastComponent,
  TimePickerComponent,
  TimePickerDirective,
  ToastComponent,
  ...Typography.Declarations,
];

const MATERIAL_MODULES = [
  MatButtonToggleModule,
  MatDatepickerModule,
  MatDialogModule,
  MatGridListModule,
  MatIconModule,
  MatButtonModule,
  MatFormFieldModule,
  MatInputModule,
  MatCheckboxModule,
  MatListModule,
  MatMenuModule,
  MatProgressSpinnerModule,
  MatSelectModule,
  MatNativeDateModule
];

@NgModule({
  declarations: [
    ...COMPONENTS
  ],
  imports: [
    ButtonsModule,
    CommonModule,
    CoreModule,
    IrisButtonModule,
    IrisCheckboxModule,
    IrisIconComponent,
    FormsModule,
    ReactiveFormsModule,
    JourneyModule,
    PortalModule,
    OverlayModule,
    NgxMaskDirective,
    SonderModalModule,
    IrisMenuModule,
    IrisIconButtonModule,
    ...MATERIAL_MODULES,
    MatButtonModule,
  ],
  exports: [
    ButtonsModule,
    JourneyModule,
    FormsModule,
    OverlayModule,
    NgxMaskDirective,
    SonderModalModule,
    ...COMPONENTS,
    ...MATERIAL_MODULES
  ],
  providers: [
    NgForm,
    UserProfileHttpService,
    provideNgxMask()
  ]
})
export class DesignModule {
  constructor(
    sanitizer: DomSanitizer,
    registry: MatIconRegistry
  ) {
    /* make these available via angular.json:
       "your-project": {
          "assets": [
            "projects/your-project/src/assets",
            {
              "glob": "** /*", (without the space for commenting)
              "input": "projects/core/src/assets",
              "output": "./assets"
            }
            ...
    */

    registry.addSvgIcon('card-brand-visa', sanitizer.bypassSecurityTrustResourceUrl('assets/card-logos/visa.svg'));
    registry.addSvgIcon('card-brand-american-express', sanitizer.bypassSecurityTrustResourceUrl('assets/card-logos/amex.svg'));
    registry.addSvgIcon('card-brand-mastercard', sanitizer.bypassSecurityTrustResourceUrl('assets/card-logos/mastercard.svg'));
    registry.addSvgIcon('card-brand-discover', sanitizer.bypassSecurityTrustResourceUrl('assets/card-logos/discover.svg'));
    registry.addSvgIcon('card-brand-diners-club', sanitizer.bypassSecurityTrustResourceUrl('assets/card-logos/diners.svg'));
    registry.addSvgIcon('card-brand-jcb', sanitizer.bypassSecurityTrustResourceUrl('assets/card-logos/jcb.svg'));
    registry.addSvgIcon('card-brand-default', sanitizer.bypassSecurityTrustResourceUrl('assets/card-logos/default.svg'));
    registry.addSvgIcon('sm-logo-mark', sanitizer.bypassSecurityTrustResourceUrl('assets/sm-logos/logomark.svg'));
    registry.addSvgIcon('sm-contact-support', sanitizer.bypassSecurityTrustResourceUrl('assets/nav/support.svg'));
  }
}
